export interface MfaEnrollmentStartResponse {
	allowEmailOtp: boolean;
	allowAuthenticatorApp: boolean;
	customerMfaPolicy: number;
	gracePeriodActive: boolean;
	gracePeriodEndsAt: string;
	canSkip: boolean;
	enrolledMethodCount: number;
	userEmail: string;
	emailVerified: boolean;
}

export interface MfaTotpSetupResponse {
	provisioningUri: string;
	manualEntryKey: string;
}

export interface MfaEnrollmentMethodResponse {
	method: string;
	enrolledAt: string;
	isPreferred: boolean;
}

/**
 * v2/MfaEnrollment/Email/Send. The route used to return an empty 200; it now reports the resend delay so
 * the wizard can run the same countdown the challenge dialog does, instead of leaving the button live.
 */
export interface MfaEmailSendResponse {
	otpExpiresAt: string;
	retryAfterSeconds: number;
}

export interface MfaChallengeStartResponse {
	sessionId: string;
	otpExpiresAt: string;
	maskedEmail: string;
	method: string;
	otpTtlMinutes: number;
	otpResendDelaySeconds: number;
	maxAttempts: number;
}

export interface MfaChallengeVerifyResponse {
	success: boolean;
	mfaSessionToken: string | null;
	attemptsRemaining: number | null;
	lockedOut: boolean;
	contactAdminPhoneEnabled: boolean;
	contactAdminPhone: string | null;
	contactAdminEmailEnabled: boolean;
	contactAdminEmail: string | null;
}

export interface MfaChallengeResendResponse {
	otpExpiresAt: string;
	retryAfterSeconds: number;
}

export interface MfaChallengeSwitchResponse {
	alternativeMethod: string;
	maskedEmail: string | null;
	otpExpiresAt: string | null;
	otpTtlMinutes: number;
	otpResendDelaySeconds: number;
}

export interface MfaCustomerSwitchEvaluation {
	challengeRequired: boolean;
	challengeSessionId: string | null;
	preferredMethod: string;
	availableMethods: string[];
	maskedEmail: string;
	otpTtlMinutes: number;
	otpResendDelaySeconds: number;
	maxAttempts: number;
}

export interface ProfileEmailBeginChangeResponse {
	requiresVerification: boolean;
	maskedEmail: string;
	otpExpiresAt: string;
	otpTtlMinutes: number;
	otpResendDelaySeconds: number;
}

export interface ProfileEmailConfirmChangeResponse {
	emailSaved: boolean;
}

export interface MfaMethodDisabledNotificationData {
	disabledMethodNames: string[];
	effectivePolicy: number;
	gracePeriodActive: boolean;
	gracePeriodDaysRemaining: number | null;
	allowedMethodsForEnrollment: string[];
	contactAdminPhone: string | null;
	contactAdminEmail: string | null;
}

export interface MfaMethodManagementResponse {
	methods: MfaEnrolledMethodDto[];
	canAddMethod: boolean;
	availableMethodsForEnrollment: string[];
	showDualEnrollmentNudge: boolean;
	effectivePolicy: number;
	allMethodsAdminDisabled: boolean;
}

export interface MfaEnrolledMethodDto {
	pkId: number;
	method: string;
	displayName: string;
	enrolledAt: string | null;
	lastChallengeAt: string | null;
	isPreferred: boolean;
	isAdminDisabled: boolean;
	availableActions: string[];
	showLastMethodWarning: boolean;
}

export interface MfaDeleteMethodResponse {
	deleted: boolean;
	remainingMethodCount: number;
}

export interface MfaReAuthBeginResponse {
	challengeRequired: boolean;
	challengeSessionId: string | null;
	preferredMethod: string;
	availableMethods: string[];
	maskedEmail: string;
	otpTtlMinutes: number;
	otpResendDelaySeconds: number;
	maxAttempts: number;
	contactAdminPhone: string | null;
	contactAdminEmail: string | null;
}

export interface MfaReAuthVerifyResponse {
	success: boolean;
	reAuthToken: string | null;
	attemptsRemaining: number | null;
	lockedOut: boolean;
	contactAdminPhoneEnabled: boolean;
	contactAdminPhone: string | null;
	contactAdminEmailEnabled: boolean;
	contactAdminEmail: string | null;
}

// Reason shown on the login modal when MFA enrollment cannot be started and the session is torn down
// (F-198). Shaped like an HttpErrorResponse ({ error: { generalMsg, detailedMsg } }) because that is what
// LoginModalComponent reads (data.error.error.detailedMsg). Shared by the guard and login-modal teardown sites.
export const ENROLLMENT_FAILED_REASON = {
	error: {
		generalMsg: 'MFA enrollment unavailable',
		detailedMsg: 'We could not start multi-factor authentication enrollment. Please sign in and try again.'
	}
};
